import { Box, Divider, List, ListItem } from "@material-ui/core"
import { useEffect } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import { useHistory } from "react-router"
import { Link } from "react-router-dom"
import type { Action } from "redux"

import { useTranslation } from "react-i18next"
import { useBrandName } from "../../../hooks"
import type { MapDto } from "../../../models"
import {
  fetchAllRegions,
  fetchChapterList,
  fetchMapList,
  selectMap,
  selectRegion,
  selectSubRegion
} from "../../../redux"
import type { AppState, AppThunkDispatch, Chapter, Region } from "../../../redux"
import { useAuthenticatedUser } from "../../indentity-provider.component"
import type { IContentTableListItem } from "../../molecules"
import { ContentTableList } from "../../molecules"

export interface IContentTableProps {
  chapters?: Chapter[]
  regions?: Region[]
  maps?: MapDto[]
  selectedRegionId?: string
  selectedSubRegionId?: string
  selectedMapId?: string
  fetchRegions: () => void
  fetchSubRegions: (regionId: string) => void
  fetchMaps: (subRegionId: string) => void
  selectRegion: (regionId: string) => void
  selectSubRegion: (subRegionId: string) => void
  selectMap: (mapId: string) => void
}

function ContentTableView(props: IContentTableProps): ReactElement {
  const {
    chapters,
    regions,
    maps,
    fetchRegions,
    fetchSubRegions: fetchRegionSubRegions,
    selectRegion,
    selectSubRegion,
    selectMap,
    selectedRegionId: selectedChapterId,
    selectedSubRegionId: selectedRegionId,
    selectedMapId,
    fetchMaps
  } = props

  const { t } = useTranslation("contentTable")
  const history = useHistory()
  const user = useAuthenticatedUser()

  const brandName: string = useBrandName()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (!chapters) {
      fetchRegions()
    }
  }, [chapters])

  function loadListFromChapter(listItem: IContentTableListItem): void {
    if (selectedChapterId !== listItem.id) {
      selectRegion(listItem.id)
      fetchRegionSubRegions(listItem.id)
    }
  }

  function loadListFromRegion(listItem: IContentTableListItem): void {
    if (selectedRegionId !== listItem.id) {
      selectSubRegion(listItem.id)
      fetchMaps(listItem.id)
    }
  }

  function mapsToContentListItem(): IContentTableListItem[] {
    return maps
      ? maps.map((map: MapDto) => {
          return {
            id: map.id,
            name: map.title as string
          }
        })
      : []
  }

  function loadMap(listItem: IContentTableListItem): void {
    selectMap(listItem.id)
    history.push(`/map/${listItem.id}`)
  }

  return (
    <>
      <Box>
        <List>
          <ListItem>
             <h3>{t("welcome", { name: user.fullName })}</h3>
          </ListItem>
          <Divider />
          <ListItem>
            <Link to={`/Mijn${brandName}`}>{t("myAtlas", { brandName })}</Link>
          </ListItem>
          <ListItem>
            <Link to="/search">{t("searchPage")}</Link>
          </ListItem>
          <ListItem>
            <Link to="/manage/map">{t("mapManager")}</Link>
          </ListItem>
          <ListItem>
            <Link to="/manage">{t("contentManager")}</Link>
          </ListItem>
        </List>
        <ContentTableList
          showArrows={true}
          onClick={loadListFromChapter}
          selectedId={selectedChapterId}
          contentList={chapters ? chapters.map(chapterToContentTableItem) : []}
        />
      </Box>
      {selectedChapterId && (
        <ContentTableList
          showArrows={true}
          onClick={loadListFromRegion}
          selectedId={selectedRegionId}
          contentList={regions ? regions.map(regionToContentTableItem) : []}
        />
      )}
      {selectedRegionId && (
        <ContentTableList selectedId={selectedMapId} contentList={mapsToContentListItem()} onClick={loadMap} />
      )}
    </>
  )
}

function chapterToContentTableItem(region: Chapter): IContentTableListItem {
  return {
    ...region,
    id: region.id as string,
    name: region.name
  }
}

function regionToContentTableItem(region: Region): IContentTableListItem {
  return {
    ...region,
    id: region.id as string,
    name: region.name
  }
}

function mapStateToProps(state: AppState) {
  return {
    regions: state.region.regions,
    chapters: state.chapters.chapters,
    maps: state.maps.maps,
    selectedRegionId: state.contentTable.selectedRegionId,
    selectedSubRegionId: state.contentTable.selectedSubRegionId,
    selectedMapId: state.contentTable.selectedMapId
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<Action>) {
  return {
    fetchRegions: () => dispatch(fetchAllRegions()),
    fetchChapters: () => {
      dispatch(fetchChapterList())
    },
    selectRegion: (regionId: string) => dispatch(selectRegion(regionId)),
    fetchMaps: (subRegionId: string) => {
      dispatch(fetchMapList(subRegionId))
    },
    selectSubRegion: (subRegionId: string) => {
      dispatch(selectSubRegion(subRegionId))
    },
    selectMap: (mapId: string) => dispatch(selectMap(mapId))
  }
}

export const ContentTable = connect(mapStateToProps, mapDispatchToProps)(ContentTableView)
